import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { DestinationPickerOverlay } from 'components/DestinationPickerOverlay';
import { addDays, format } from 'date-fns';
import { OpenedFormControl, RecentSearchParams, useSearchBox, useSearchBoxDatePicker, useSearchBoxDestinationPicker, useSearchBoxGuestPicker } from 'hooks/searchBox';
import { usePageLoadEndTimer } from 'hooks/usePageLoad';
import { getDevicePlatform, getMeta } from 'modules/meta/selectors';
import { getSearchParameters, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getIsMultiRoomBundlesSearch } from 'modules/search/selectors';
import { getOpenDatePickerType, getSearchBoxDestinationDisplayValue, getSearchBoxValues } from 'modules/searchBox/selectors';
import { closeSearchBox } from 'modules/searchBox/slice';
import { SearchTrigger } from 'modules/searchBox/types';
import { getUserSearchHistory } from 'utils/persistence';
import { DatePickerType } from '@daedalus/core/src/datePicker/types';
import { ConfigureI18nContext } from '@daedalus/core/src/localization/components/ConfigureI18n';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { numberOfGuests, roomsSplitToString, roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { RoomConfigType, RoomSplitType } from '@daedalus/core/src/room/types/RoomConfiguration';
import { WithColorPriceDayData } from '@daedalus/core/src/search/modules/ColorPriceCalendar/components/WithColorPriceDayData';
import { useColorPriceCalendar } from '@daedalus/core/src/search/modules/ColorPriceCalendar/hooks';
import { dateToMiddayDate, UTS_DATE_FORMAT } from '@daedalus/core/src/utils/date';
import { DatePickerOverlay } from '@daedalus/shared/src/search/DatePickerOverlay';
import { GuestRoomConfigOverlay } from '@daedalus/shared/src/search/GuestRoomConfig/GuestRoomConfigOverlay';
import { SearchFormVertical } from '@daedalus/shared/src/search/SearchFormVertical';
const SearchBoxWrapperBase = /*#__PURE__*/styled('div')({
  name: "SearchBoxWrapperBase",
  class: "sy9q1db",
  propsAsIs: false
});
export const SearchFormMobile = () => {
  const dispatch = useDispatch();
  const isAndroid = useSelector(getDevicePlatform) === 'android';
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const [openedFormControl, setOpenedFormControl] = useState<OpenedFormControl>(OpenedFormControl.NONE);
  const [scrollY, setScrollY] = useState(null);
  const [isGeolocationSearchSelected, setIsGeolocationSearchSelected] = useState(false);

  // Use the value from the searchbox as the value in Search won't
  // get set until the search has been made and the URL param has been added
  const {
    optimizeRooms
  } = useSelector(getSearchBoxValues);
  const isOptimizeRooms = Boolean(optimizeRooms);
  const userSearchHistory = getUserSearchHistory();
  const latestUserSearch = userSearchHistory?.[0];
  const today = dateToMiddayDate(new Date());
  const tomorrow = addDays(today, 1);
  const todayString = format(today, UTS_DATE_FORMAT);
  const tomorrowString = format(tomorrow, UTS_DATE_FORMAT);
  const initialDatePickerValues = {
    checkIn: latestUserSearch?.searchParams.checkIn || todayString,
    checkOut: latestUserSearch?.searchParams.checkOut || tomorrowString
  };
  const initialGuestPickerValues = {
    rooms: latestUserSearch?.searchParams.rooms,
    optimizeRooms: latestUserSearch?.searchParams.optimizeRooms
  };
  const {
    doSearch,
    onSearchButtonClick
  } = useSearchBox();
  const {
    destinationHasError,
    destinationErrorMessage,
    onDestinationChange,
    onDestinationPickerInputClick,
    onRecentSearchSelected,
    setDestinationErrorMessage,
    setDestinationHasError,
    destinationDisplayValue,
    destinationPickerInputDisplayValue,
    onSuggestionsRequested,
    onNearbyPropertiesSuggestionSelected
  } = useSearchBoxDestinationPicker();
  const {
    onDateChange,
    onDatePickerClose,
    onDatePickerDayClick,
    onDatePickerOpen,
    onDatePickerInputClick,
    onDatePickerSubmit,
    onDatePickerReset
  } = useSearchBoxDatePicker(initialDatePickerValues);
  const {
    onGuestRoomsChange,
    onGuestOptimizeRoomsChange,
    onGuestPickerClick,
    onGuestPickerSubmit,
    onGuestPickerReset,
    onGuestPickerDisplayed,
    onGuestPickerOptionInfoDisplayed,
    onGuestConfigPreferenceClick
  } = useSearchBoxGuestPicker(initialGuestPickerValues);
  const {
    checkIn,
    checkOut,
    hotelId,
    placeId,
    address,
    rooms
  } = useSelector(getSearchBoxValues);
  const searchBoxDestinationDisplayValue = useSelector(getSearchBoxDestinationDisplayValue);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const searchParameters = useSelector(getSearchParameters);
  const combinedSearchParameters = {
    ...searchParameters,
    rooms,
    checkIn,
    checkOut
  };
  const {
    includeLocalTaxes,
    includeTaxes
  } = useSelector(getMeta);
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const pricingContext = {
    includeTaxes,
    includeLocalTaxes,
    includeRoomsInNightlyPrice: isMultiRoomBundlesSearch
  };
  const numberOfGuestsValue = numberOfGuests(rooms);
  const isColorPriceDatePickerEnabled = numberOfGuestsValue <= 2 && Boolean(hotelId);
  const {
    monthNames,
    weekDayNamesShort,
    firstDayOfWeek
  } = useContext(ConfigureI18nContext);
  const {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    roomsSplit
  } = roomsToConfigurationObject(rooms);
  const openedDatePickerType = useSelector(getOpenDatePickerType);
  const isDestinationPickerOpen = useMemo(() => openedFormControl === OpenedFormControl.DESTINATION, [openedFormControl]);
  const isDatePickerOpen = useMemo(() => openedFormControl === OpenedFormControl.DATE_PICKER, [openedFormControl]);
  const isGuestPickerOpen = useMemo(() => openedFormControl === OpenedFormControl.GUEST, [openedFormControl]);
  const {
    requestOneMonthAvailability
  } = useColorPriceCalendar({
    searchParameters: combinedSearchParameters,
    pricingContext
  });
  const handleSuggestionSelected = () => {
    restoreScrollPosition();
    setIsGeolocationSearchSelected(false);
    openDatePickerControl(DatePickerType.CheckIn);
  };
  const handleRecentSearchSelected = useCallback((recentSearch: RecentSearchParams): void => {
    setIsGeolocationSearchSelected(false);
    onRecentSearchSelected({
      recentSearch,
      additionalValues: {
        homeSearch: '1'
      }
    });
  }, [onRecentSearchSelected, setIsGeolocationSearchSelected]);
  const handleDestinationPickerClick = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.DESTINATION);
    setDestinationHasError(false);
    setScrollY(window.scrollY);
    onDestinationPickerInputClick();
    destinationInputRef?.current?.focus?.();
    window.scrollTo(0, 0);
  }, [setDestinationHasError, onDestinationPickerInputClick]);
  const openDatePickerControl = useCallback((type: DatePickerType): void => {
    setOpenedFormControl(OpenedFormControl.DATE_PICKER);
    onDatePickerInputClick(type);
    onDatePickerOpen(type);
  }, [onDatePickerOpen, onDatePickerInputClick]);
  const handleDatePickerClose = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.NONE);
    onDatePickerClose();
    onDatePickerReset();
  }, [onDatePickerReset, onDatePickerClose]);
  const handleDatePickerSubmit = useCallback((): void => {
    onDatePickerSubmit();
    handleCloseOverlays();
  }, [onDatePickerSubmit]);
  const handleGuestPickerClick = useCallback((): void => {
    setOpenedFormControl(OpenedFormControl.GUEST);
    onGuestPickerClick();
    onGuestPickerDisplayed();
  }, [onGuestPickerClick, onGuestPickerDisplayed]);
  const handleCloseGuestPicker = () => {
    onGuestPickerReset();
    setOpenedFormControl(OpenedFormControl.NONE);
  };
  const handleGuestRoomsChange = useCallback((roomsSplit: RoomSplitType[]): void => {
    const rooms = roomsSplitToString(roomsSplit);
    onGuestRoomsChange(rooms);
  }, [onGuestRoomsChange]);
  const handleGuestOptimizeRoomsChange = useCallback((isOptimizeRooms?: boolean): void => {
    onGuestOptimizeRoomsChange(isOptimizeRooms);
  }, [onGuestOptimizeRoomsChange]);
  const handleGuestPickerSubmit = useCallback((rooms: RoomSplitType[]): void => {
    onGuestPickerSubmit();
    handleCloseOverlays();
    handleGuestRoomsChange(rooms);
  }, [handleGuestRoomsChange, onGuestPickerSubmit]);
  const restoreScrollPosition = useCallback(() => {
    if (!scrollY) return;
    window.scrollTo(0, scrollY);
    setScrollY(null);
  }, [scrollY]);
  const handleCloseOverlays = () => {
    restoreScrollPosition();
    setOpenedFormControl(OpenedFormControl.NONE);
  };
  const handleSubmit = useCallback((event?: React.SyntheticEvent | null | undefined): void | boolean => {
    event?.preventDefault();
    if (!placeId && !hotelId && !isGeolocationSearchSelected && !address) {
      setDestinationHasError(true);
      setDestinationErrorMessage(searchBoxDestinationDisplayValue ? 'destinationPickerUnknownMessage' : 'destinationPickerEmptyMessage');
      return false;
    }
    onSearchButtonClick();
    dispatch(closeSearchBox());
    doSearch({
      newUrlParams: {
        homeSearch: '1'
      },
      searchTrigger: SearchTrigger.Submit
    });
  }, [placeId, hotelId, isGeolocationSearchSelected, address, onSearchButtonClick, doSearch, setDestinationHasError, searchBoxDestinationDisplayValue]);
  const handleNearbyPropertiesSuggestionSelected = useCallback(() => {
    onNearbyPropertiesSuggestionSelected(false);
    setIsGeolocationSearchSelected(true);
    openDatePickerControl(DatePickerType.CheckIn);
  }, [onNearbyPropertiesSuggestionSelected, openDatePickerControl]);
  usePageLoadEndTimer();
  return <SearchBoxWrapperBase>
      <SearchFormVertical destination={destinationDisplayValue} checkIn={checkIn} checkOut={checkOut} numberOfAdults={numberOfAdults} numberOfChildren={numberOfChildren} numberOfRooms={numberOfRooms} onDatePickerClick={openDatePickerControl} onDestinationPickerClick={handleDestinationPickerClick} onGuestPickerClick={handleGuestPickerClick} onSubmit={handleSubmit} destinationHasError={destinationHasError} destinationErrorMessage={destinationErrorMessage} isRoomsLabelShown={!isOptimizeRooms} />
      <DestinationPickerOverlay isOpen={isDestinationPickerOpen} displayValue={destinationPickerInputDisplayValue} onSuggestionsRequested={onSuggestionsRequested} onChange={onDestinationChange} onSuggestionSelected={handleSuggestionSelected} onRecentSearchSelected={handleRecentSearchSelected} onClose={handleCloseOverlays} onNearbyPropertiesSuggestionSelected={handleNearbyPropertiesSuggestionSelected} destinationInputRef={destinationInputRef} />
      <DatePickerOverlay virtualize={isAndroid} isOpen={isDatePickerOpen} checkIn={checkIn} checkOut={checkOut} firstDayOfWeek={firstDayOfWeek} months={monthNames} weekdaysShort={weekDayNamesShort} openedDatePickerType={openedDatePickerType} onDayClick={onDatePickerDayClick} onOpen={onDatePickerOpen} onClose={handleDatePickerClose} onChange={onDateChange} onSubmit={handleDatePickerSubmit} submitButtonMessage={<FormattedMessageWrapper id="apply" defaultMessage="Apply" />} showTotalPrices={showTotalPrices} shouldShowPrices onMonthView={date => requestOneMonthAvailability(date, [hotelId])} isColorPriceDatePickerEnabled={isColorPriceDatePickerEnabled} DayInnerContainer={WithColorPriceDayData} />
      <GuestRoomConfigOverlay isOpen={isGuestPickerOpen} roomsSplit={roomsSplit} onClose={handleCloseGuestPicker} onRoomsChange={handleGuestRoomsChange} onOptimizeRoomsChange={handleGuestOptimizeRoomsChange} onSubmit={handleGuestPickerSubmit} onInfoOpen={onGuestPickerOptionInfoDisplayed} onGuestConfigOptionClick={onGuestConfigPreferenceClick} roomConfigType={optimizeRooms ? RoomConfigType.Optimized : RoomConfigType.Manual} submitButtonMessage={<FormattedMessageWrapper id="apply" defaultMessage="Apply" />} />
    </SearchBoxWrapperBase>;
};

require("./SearchFormMobile.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchFormMobile.tsx");